import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Routes that must always render in light mode regardless of user preference.
const FORCE_LIGHT_PATHS = ['/login', '/signup', '/forgot-password'];

function isForceLightPath(pathname: string): boolean {
  return FORCE_LIGHT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, userProfile } = useAuth();

  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem('simcheck-theme');
    return (stored as Theme) || 'dark';
  });

  const [pathname, setPathname] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  // Track the UID we last hydrated theme from so we only pull the stored preference
  // once per user-login, and local toggles after that don't get overwritten until
  // the user logs out + back in.
  const hydratedForUidRef = useRef<string | null>(null);

  // Track pathname changes so the theme effect re-runs on route transitions.
  useEffect(() => {
    const update = () => setPathname(window.location.pathname);

    // Catch back/forward navigation.
    window.addEventListener('popstate', update);

    // react-router uses history.pushState / replaceState — patch to emit events.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const result = origPush.apply(this, args as any);
      update();
      return result;
    };
    window.history.replaceState = function (...args) {
      const result = origReplace.apply(this, args as any);
      update();
      return result;
    };

    return () => {
      window.removeEventListener('popstate', update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  // When userProfile loads (or switches user), adopt that user's saved theme.
  useEffect(() => {
    if (!currentUser) {
      hydratedForUidRef.current = null;
      return;
    }
    if (hydratedForUidRef.current === currentUser.uid) return;

    const savedTheme = userProfile?.preferences?.theme;
    if (savedTheme === 'dark' || savedTheme === 'light') {
      hydratedForUidRef.current = currentUser.uid;
      setThemeState(savedTheme);
    }
  }, [currentUser, userProfile]);

  useEffect(() => {
    const root = document.documentElement;
    const applied: Theme = isForceLightPath(pathname) ? 'light' : theme;

    root.classList.remove('dark', 'light');
    root.classList.add(applied);

    // Only persist the user's actual preference, not the forced override.
    localStorage.setItem('simcheck-theme', theme);
  }, [theme, pathname]);

  const persistToFirestore = (newTheme: Theme) => {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    // Fire-and-forget — UI is already updated via local state.
    updateDoc(userRef, { 'preferences.theme': newTheme }).catch((err) => {
      console.error('Failed to persist theme preference to user profile:', err);
    });
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    persistToFirestore(newTheme);
  };

  const toggleTheme = () => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      persistToFirestore(next);
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
